import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../../constants";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { trackEvent } from "../../utilities/analyticsService";

let lastTrackedPage = { key: "", time: 0 };
let lastTrackedClick = { key: "", time: 0 };
const SUPER_ADMIN_USER_ID = "1";

const ignoredRoutes = [
	ROUTES.LOGOUT,
];

const IGNORED_CLICK_LABELS = new Set(["close"]);

const buildSiteContext = (site = {}) => {
	if (!site || Array.isArray(site)) return {};
	return {
		customer_id: site.cus_id ?? site.customer_id ?? "",
		customer_name: site.parent_customer_name ?? site.customer_name ?? "",
		site_id: site.site_id ?? "",
		site_name: site.customer_site_name ?? site.site_name ?? "",
		location: site.address_name_ln ?? site.location ?? site.region ?? site.country ?? "",
	};
};

const getClickTarget = (target) => {
	if (!target?.closest) return null;
	return target.closest(
		"button, a, [role='button'], [role='menuitem'], input[type='button'], input[type='submit'], .MuiButtonBase-root"
	);
};

const getElementLabel = (element) => {
	if (!element) return "";
	const label =
		element.getAttribute("data-analytics-label") ||
		element.getAttribute("aria-label") ||
		element.getAttribute("title") ||
		element.innerText ||
		element.textContent ||
		element.value ||
		"";
	return String(label).replace(/\s+/g, " ").trim().slice(0, 120);
};

const getActionCategory = (path, element) => {
	const configured = element?.getAttribute?.("data-analytics-category");
	if (configured) return configured;
	if (path.startsWith("/admin")) return "admin";
	if (path.startsWith("/machines")) return "machine";
	if (path.startsWith("/checkout")) return "checkout";
	if (path.startsWith("/cart")) return "cart";
	return element?.tagName?.toLowerCase() || "click";
};

const isSuperAdminUser = (user = {}) =>
	String(user?.user_id ?? user?.id ?? user?.profileId ?? "").trim() === SUPER_ADMIN_USER_ID;

const buildUserContext = (user = {}) => ({
	user_id: user?.user_id ?? "",
	user_name: user?.user_display_name || user?.user_name || "",
	user_email: user?.user_email || "",
	user_role: user?.role?.role_name || user?.user_role_name || user?.user_role || "",
});

const OpenPanelPageTracker = () => {
	const location = useLocation();
	const { user, loading, isAuthenticated } = useAuth();
	const { selectedSiteData, selectedSiteKey, loading: siteLoading } = useSite();

	useEffect(() => {
		if (loading) return;
		if (siteLoading) return;
		if (ignoredRoutes.some((route) => location.pathname.startsWith(route))) return;
		if (isSuperAdminUser(user)) return;

		const path = `${location.pathname}${location.search}${location.hash}`;
		const now = Date.now();
		const userScope = user?.user_id ? `user:${user.user_id}` : "visitor";
		const pageKey = `${userScope}|${path}`;
		if (lastTrackedPage.key === pageKey && now - lastTrackedPage.time < 1500) return;
		lastTrackedPage = { key: pageKey, time: now };

		trackEvent("page_viewed", {
			path,
			...buildUserContext(user),
			...buildSiteContext(selectedSiteData),
		});
	}, [loading, location.hash, location.pathname, location.search, selectedSiteData, selectedSiteKey, siteLoading, user]);

	useEffect(() => {
		if (loading) return;
		if (siteLoading) return;
		if (ignoredRoutes.some((route) => location.pathname.startsWith(route))) return;
		if (isSuperAdminUser(user)) return;

		const handleClick = (event) => {
			const element = getClickTarget(event.target);
			if (!element) return;

			const label = getElementLabel(element);
			if (IGNORED_CLICK_LABELS.has(label.toLowerCase())) return;

			const path = `${location.pathname}${location.search}${location.hash}`;
			const eventName = location.pathname.startsWith("/admin") ? "admin_action_clicked" : "user_click";
			const actionCategory = getActionCategory(location.pathname, element);
			const userScope = user?.user_id ? `user:${user.user_id}` : "visitor";
			const clickKey = `${userScope}|${eventName}|${path}|${label}|${actionCategory}`;
			const now = Date.now();
			if (lastTrackedClick.key === clickKey && now - lastTrackedClick.time < 500) return;
			lastTrackedClick = { key: clickKey, time: now };

			trackEvent(eventName, {
				path,
				element_label: label,
				element_tag: element.tagName?.toLowerCase() || "",
				action_category: actionCategory,
				...buildUserContext(user),
				...buildSiteContext(selectedSiteData),
			});
		};

		document.addEventListener("click", handleClick, true);
		return () => document.removeEventListener("click", handleClick, true);
	}, [loading, location.hash, location.pathname, location.search, selectedSiteData, selectedSiteKey, siteLoading, user]);

	return null;
};

export default OpenPanelPageTracker;
